import {
  Box,
  Checkbox,
  Chip,
  Divider,
  List,
  ListItem,
  Typography,
  Avatar,
  Stack,
} from "@mui/material";
import {
  Job,
  colorByStatus,
  statusToLabel,
} from "../../../pages/playground/types";
import { useGo } from "@refinedev/core";

type GroupedJobs = Record<string, Job[]>;

interface JobListViewProps {
  jobs: Job[];
  groupedJobs?: GroupedJobs | null;
  showBulkActions?: boolean;
  selectedJobs?: Set<string>;
  onJobSelectionChange?: (jobId: string, selected: boolean) => void;
  onSelectAll?: (selected: boolean) => void;
}

export default function JobListView({
  jobs,
  groupedJobs = null,
  showBulkActions = false,
  selectedJobs = new Set(),
  onJobSelectionChange,
  onSelectAll,
}: JobListViewProps) {
  const go = useGo();

  const handleJobClick = (job: Job) => {
    if (showBulkActions) {
      // In bulk mode, clicking anywhere toggles selection
      const isSelected = selectedJobs.has(job.id);
      onJobSelectionChange?.(job.id, !isSelected);
      return;
    }

    const params = new URLSearchParams();
    params.set("jobId", job.id);

    go({
      to: "/editor",
      query: Object.fromEntries(params),
    });
  };

  const handleSelectAllChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onSelectAll?.(event.target.checked);
  };

  const isAllSelected =
    jobs.length > 0 && jobs.every((job) => selectedJobs.has(job.id));
  const isIndeterminate =
    jobs.some((job) => selectedJobs.has(job.id)) && !isAllSelected;

  // Helper to render the job list structure for a given set of jobs
  const renderJobList = (listJobs: Job[]) => (
    <List sx={{ width: "100%", py: 0 }}>
      {showBulkActions && listJobs.length > 0 && (
        <ListItem sx={{ py: 0.5, backgroundColor: "grey.50", minHeight: 40 }}>
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            <Checkbox
              checked={isAllSelected}
              indeterminate={isIndeterminate}
              onChange={handleSelectAllChange}
              size="small"
              sx={{ mr: 1, p: 0.5 }}
            />
            <Typography
              variant="body2"
              sx={{ fontWeight: "medium", fontSize: "0.875rem" }}
            >
              Select All ({listJobs.length} jobs)
            </Typography>
          </Box>
        </ListItem>
      )}

      {listJobs.map((job, index) => (
        <Box key={job.id}>
          <ListItem
            sx={{
              py: 0.5,
              minHeight: 56,
              cursor: "pointer",
              transition: "background-color 0.2s",
              "&:hover": {
                backgroundColor: showBulkActions
                  ? "rgba(0, 0, 0, 0.02)"
                  : "rgba(0, 0, 0, 0.04)",
              },
              display: "flex",
              justifyContent: "space-between",
            }}
            onClick={() => handleJobClick(job)}
          >
            {/* Left side with optional checkbox, SKU and images */}
            <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              {showBulkActions && (
                <Checkbox
                  checked={selectedJobs.has(job.id)}
                  onChange={(e) =>
                    onJobSelectionChange?.(job.id, e.target.checked)
                  }
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                  sx={{ mr: 1, p: 0.5 }}
                />
              )}

              {/* SKU */}
              <Typography
                variant="body2"
                sx={{
                  minWidth: 80,
                  fontWeight: "medium",
                  color: "text.primary",
                  mr: 1,
                  fontSize: "0.875rem",
                }}
              >
                {job.master_id || "No SKU"}
              </Typography>

              {/* Thumbnails */}
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ overflow: "auto", mr: 1 }}
              >
                {job.thumbnails?.length ? (
                  job.thumbnails.map((img, imgIndex) => (
                    <Avatar
                      key={imgIndex}
                      variant="rounded"
                      src={img}
                      alt={`${job.title || "Product"} ${imgIndex + 1}`}
                      sx={{ width: 40, height: 40, borderRadius: 0.5 }}
                    />
                  ))
                ) : (
                  <Avatar
                    variant="rounded"
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 0.5,
                      fontSize: "0.65rem",
                      bgcolor: "grey.200",
                      color: "grey.600",
                    }}
                  >
                    No img
                  </Avatar>
                )}
              </Stack>

              {/* Garment Type */}
              {job.garment_type && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    mr: 1,
                    fontSize: "0.8rem",
                  }}
                >
                  {job.garment_type}
                </Typography>
              )}
            </Box>

            {/* Status chip and role indicators */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* Role indicators */}
              {(job.is_editor || job.is_reviewer) && (
                <Box
                  sx={{
                    backgroundColor: job.is_reviewer
                      ? "secondary.main"
                      : "primary.main",
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: job.is_reviewer
                        ? "secondary.contrastText"
                        : "primary.contrastText",
                    }}
                  >
                    {job.is_editor && !job.is_reviewer && "Editor"}
                    {job.is_reviewer && !job.is_editor && "Reviewer"}
                    {job.is_editor && job.is_reviewer && "Editor & Reviewer"}
                  </Typography>
                </Box>
              )}

              {/* Status chip */}
              <Chip
                label={statusToLabel(job.status)}
                size="small"
                sx={{
                  backgroundColor: colorByStatus[job.status],
                  color: "white",
                  fontWeight: "medium",
                }}
              />
            </Box>
          </ListItem>
          {index < listJobs.length - 1 && <Divider component="li" />}
        </Box>
      ))}
    </List>
  );

  const hasGroups = groupedJobs && Object.keys(groupedJobs).length > 0;

  return (
    <Box sx={{ mt: 2 }}>
      {!hasGroups && jobs.length === 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No jobs available
          </Typography>
        </Box>
      )}

      {hasGroups && groupedJobs
        ? Object.entries(groupedJobs).map(([groupKey, group]) => (
            <Box key={groupKey} sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {groupKey} ({group.length})
              </Typography>
              {renderJobList(group)}
            </Box>
          ))
        : renderJobList(jobs)}
    </Box>
  );
}
